<script setup>
import { computed, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

/**
 * Conversas do MESMO contato nas outras caixas de entrada, sem sair do
 * atendimento (card 86ajybzep).
 *
 * A lista já existia no fim da barra lateral, mas escondia de qual caixa era
 * cada conversa: o ConversationCard só mostra o nome da caixa quando NÃO há
 * caixa selecionada, e durante um atendimento sempre há. Quem atende via as
 * conversas sem saber de onde vinham. Aqui elas aparecem agrupadas pela caixa,
 * que é a informação que faltava.
 *
 * A busca não filtra por status, então cai aqui tanto o histórico encerrado
 * quanto uma conversa ABERTA agora em outra caixa. Clicar leva para lá.
 */
const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const dialogRef = ref(null);

const contactConversationsGetter = useMapGetter(
  'contactConversations/getContactConversation'
);
const hasMoreGetter = useMapGetter(
  'contactConversations/getHasMoreConversations'
);
const inboxGetter = useMapGetter('inboxes/getInbox');
const uiFlags = useMapGetter('contactConversations/getUIFlags');

const conversations = computed(() =>
  contactConversationsGetter.value(props.contactId)
);

const outrasConversas = computed(() =>
  conversations.value.filter(c => c.id !== Number(props.conversationId))
);

/**
 * Um grupo por caixa, ordenado pela conversa mais recente de cada uma: quem
 * abre isto está atrás do que aconteceu por último, não da caixa de menor id.
 */
const gruposPorCaixa = computed(() => {
  const porCaixa = new Map();
  outrasConversas.value.forEach(conversa => {
    const inboxId = conversa.inbox_id;
    if (!porCaixa.has(inboxId)) porCaixa.set(inboxId, []);
    porCaixa.get(inboxId).push(conversa);
  });

  const ultimaAtividade = lista =>
    Math.max(...lista.map(c => c.last_activity_at ?? 0));

  return [...porCaixa.entries()]
    .map(([inboxId, lista]) => ({
      inboxId,
      inbox: inboxGetter.value(inboxId) ?? {},
      conversas: [...lista].sort(
        (a, b) => (b.last_activity_at ?? 0) - (a.last_activity_at ?? 0)
      ),
    }))
    .sort(
      (a, b) => ultimaAtividade(b.conversas) - ultimaAtividade(a.conversas)
    );
});

// Espelha o ContactConversations: depende do total carregado, não do que sobra
// depois de tirar a conversa atual — uma página só com ela ainda tem histórico
// atrás.
const showLoadMore = computed(() => hasMoreGetter.value(props.contactId));
const isFetching = computed(() => uiFlags.value.isFetching);
const isFetchingMore = computed(() => uiFlags.value.isFetchingMore);

const buscar = () => {
  if (props.contactId) {
    store.dispatch('contactConversations/get', props.contactId);
  }
};

const loadMore = () => {
  store.dispatch('contactConversations/loadMore', props.contactId);
};

// Trocar de conversa com o diálogo aberto tem de trocar o conteúdo junto.
watch(
  () => props.contactId,
  (novo, anterior) => {
    if (novo && novo !== anterior) buscar();
  }
);

const open = () => {
  buscar();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('CONVERSATION.OTHER_INBOXES.TITLE')"
    :description="$t('CONVERSATION.OTHER_INBOXES.DESCRIPTION')"
    width="xl"
    overflow-y-auto
    :show-confirm-button="false"
    :cancel-button-label="$t('CONVERSATION.OTHER_INBOXES.CLOSE')"
  >
    <div v-if="isFetching" class="flex items-center justify-center py-8">
      <Spinner />
    </div>

    <div v-else class="flex flex-col gap-4 max-h-[60vh] overflow-y-auto">
      <div
        v-if="!gruposPorCaixa.length"
        class="py-8 text-center text-n-slate-11"
      >
        {{ $t('CONVERSATION.OTHER_INBOXES.EMPTY') }}
      </div>

      <div
        v-for="grupo in gruposPorCaixa"
        :key="grupo.inboxId"
        class="flex flex-col gap-1"
      >
        <div
          class="flex items-center gap-2 px-2 py-1 border-b border-n-weak sticky top-0 bg-n-solid-1 z-10"
        >
          <InboxName :inbox="grupo.inbox" />
          <span class="text-label-small text-n-slate-10">
            {{ grupo.conversas.length }}
          </span>
        </div>
        <ConversationCard
          v-for="conversa in grupo.conversas"
          :key="conversa.id"
          :chat="conversa"
          hide-inbox-name
          hide-thumbnail
          enable-context-menu
          compact
          :allowed-context-menu-options="['open-new-tab', 'copy-link']"
          @click="close"
        />
      </div>

      <!-- Fora do bloco da lista de propósito: quando a primeira página só
           traz a conversa atual, a tela fica "vazia" mas ainda há histórico
           atrás. Sem o botão aqui, não haveria como chegar nele. -->
      <div v-if="showLoadMore" class="flex justify-center pt-1">
        <NextButton
          data-testid="load-more-other-inboxes"
          :label="$t('CHAT_LIST.LOAD_MORE_CONVERSATIONS')"
          :is-loading="isFetchingMore"
          :disabled="isFetchingMore"
          slate
          sm
          outline
          @click="loadMore"
        />
      </div>
    </div>
  </Dialog>
</template>
